import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    User as UserIcon,
    HeartPulse,
    FileText,
    Stethoscope,
    Wine,
    Activity,
    Apple,
    Sun,
    AlertTriangle,
    Sparkles,
    Brain,
    Pill,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Loader2,
    List,
    LayoutGrid,
} from "lucide-react";
import { CtaButton } from "@/components/CtaButton";
import { PageHeader } from "@/components/PageHeader";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_DRAFT_ID = "choice-ay-intake-draft-id";
const LS_DRAFT_DATA = "choice-ay-intake-draft-data";

const FAMILY_OPTIONS = [
    "High blood pressure",
    "Heart disease",
    "Cancer",
    "Mental disorder",
    "Stroke",
    "Diabetes",
    "Other",
];

const PRACTICE_FREQ = [
    "None",
    "Occasional",
    "Several/month",
    "Several/week",
    "Daily",
];

const DOSHA_ROWS = [
    {
        key: "body_frame",
        label: "Body frame",
        vata: "Thin / light, hard to gain weight",
        pitta: "Medium build, moderate muscle",
        kapha: "Sturdy, larger frame, gains easily",
    },
    {
        key: "skin",
        label: "Skin",
        vata: "Dry, rough, cool",
        pitta: "Warm, oily, freckles/rashes",
        kapha: "Thick, soft, oily, cool",
    },
    {
        key: "hair",
        label: "Hair",
        vata: "Dry, frizzy, brittle",
        pitta: "Fine, oily, early greying/balding",
        kapha: "Thick, wavy, lustrous",
    },
    {
        key: "eyes",
        label: "Eyes",
        vata: "Small, dry, active",
        pitta: "Medium, sharp, sensitive to light",
        kapha: "Large, calm, moist",
    },
    {
        key: "appetite",
        label: "Appetite",
        vata: "Variable, easy to skip meals",
        pitta: "Strong, must eat on time",
        kapha: "Steady, can skip easily",
    },
    {
        key: "digestion",
        label: "Digestion",
        vata: "Irregular, bloating, gas",
        pitta: "Quick, sometimes loose, heartburn",
        kapha: "Slow, heavy after meals",
    },
    {
        key: "sleep",
        label: "Sleep",
        vata: "Light, interrupted, 4–6 hrs",
        pitta: "Sound but short, 6–8 hrs",
        kapha: "Deep, long, hard to wake",
    },
    {
        key: "activity",
        label: "Activity",
        vata: "Restless, fast, on-the-go",
        pitta: "Driven, focused, ambitious",
        kapha: "Slow, methodical, steady",
    },
    {
        key: "temperament",
        label: "Temperament",
        vata: "Enthusiastic, anxious under stress",
        pitta: "Determined, irritable under stress",
        kapha: "Calm, withdrawn under stress",
    },
    {
        key: "weather_preference",
        label: "Weather preference",
        vata: "Prefers warmth, sensitive to cold",
        pitta: "Prefers coolness, sensitive to heat",
        kapha: "Prefers dry/warm, sensitive to damp/cold",
    },
    {
        key: "memory",
        label: "Memory",
        vata: "Quick to learn, quick to forget",
        pitta: "Sharp, accurate",
        kapha: "Slow to learn, never forgets",
    },
    {
        key: "speech",
        label: "Speech",
        vata: "Fast, talkative, jumps topics",
        pitta: "Clear, articulate, persuasive",
        kapha: "Slow, melodic, deliberate",
    },
];

const MENTAL_ROWS = [
    {
        key: "mood_general",
        label: "Mood in general",
        vata: "Enthusiastic, easily worried",
        pitta: "Driven, sometimes irritable",
        kapha: "Steady, content, slow to anger",
    },
    {
        key: "response_to_stress",
        label: "Response to stress",
        vata: "Anxious, scattered",
        pitta: "Angry, critical",
        kapha: "Withdrawn, sad",
    },
    {
        key: "decision_making",
        label: "Decision-making",
        vata: "Indecisive, changes mind",
        pitta: "Decisive, structured",
        kapha: "Slow, cautious, holds tight",
    },
    {
        key: "emotional_pattern",
        label: "Emotional pattern",
        vata: "Volatile, swings quickly",
        pitta: "Intense, but recovers",
        kapha: "Even, deeply attached",
    },
];

// ---- Helpers ----
const emptyForm = () => ({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address_line1: "",
    city: "",
    state: "",
    postal_code: "",
    birthdate: "",
    age: "",
    sex_assigned_at_birth: "",
    gender_identity: "",
    occupation: "",
    marital_status: "",
    children_count: "",
    children_ages: "",
    emergency_contact: { name: "", phone: "", relationship: "" },
    how_heard: "",
    why_seeking_care: "",
    primary_concern: "",
    goals: "",
    consent: {
        informed_consent: false,
        scope_of_practice: false,
        financial_policy: false,
        informed_consent_signature: "",
        scope_signature: "",
        financial_signature: "",
        signed_at: "",
    },
    medical_history: {
        past_illnesses: "",
        hospitalizations: "",
        operations: "",
        other_conditions: "",
        pregnant_or_nursing: "",
        pregnancy_details: "",
        chronic_conditions: "",
        family_history: [],
        family_history_other: "",
    },
    substance_use: {
        smokes: "",
        smoking_details: "",
        alcohol: "",
        alcohol_frequency: "",
        recreational_drugs: "",
        drug_details: "",
        caffeine: "",
        caffeine_amount: "",
    },
    regular_practices: {
        exercise: "",
        yoga: "",
        meditation: "",
        pranayama: "",
        other_practice: "",
        other_practice_name: "",
    },
    relationship_life: {
        relationship_quality: "",
        intimacy_frequency: "",
        feels_supported: "",
        social_circle: "",
    },
    diet: {
        diet_description: "",
        appetite: "",
        meals_per_day: "",
        water_oz_per_day: "",
        organic_percent: "",
        cravings: "",
        foods_that_disagree: "",
        typical_breakfast: "",
        typical_lunch: "",
        typical_dinner: "",
        snacks: "",
    },
    daily_schedule: {
        wake_time: "",
        bed_time: "",
        morning_routine: "",
        workday_routine: "",
        evening_routine: "",
        sleep_quality: "",
        dreams: "",
    },
    allergies: { food_allergies: "", drug_allergies: "", environmental_allergies: "", other: "" },
    dosha_assessment: { notes: "" },
    mental_emotional: { stress_level: "", mental_health_history: "" },
    womens_health: {
        period_regular: "",
        last_period_date: "",
        cycle_length_days: "",
        period_symptoms: [],
        is_pregnant: "",
        has_been_pregnant: "",
        pregnancies_count: "",
        menopause_status: "",
        notes: "",
    },
    challenging_patterns: {
        digestion: [],
        elimination: [],
        emotions: [],
        other_symptoms: [],
        diagnosed_conditions: [],
    },
    medications: [],
    additional_notes: "",
});

// ---- Reusable atoms ----
const Field = ({ label, hint, required, children }) => (
    <label className="block">
        <span className="block text-sm font-medium text-foreground/85">
            {label}
            {required && <span className="text-terracotta"> *</span>}
        </span>
        {hint && <span className="block text-xs text-foreground/60">{hint}</span>}
        <div className="mt-1.5">{children}</div>
    </label>
);

const inputClass =
    "w-full rounded-xl bg-white border border-border/80 px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream";

const Text = ({ value, onChange, ...rest }) => (
    <input
        type={rest.type || "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
        {...rest}
    />
);

const TextArea = ({ value, onChange, rows = 3, ...rest }) => (
    <textarea
        value={value || ""}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
        {...rest}
    />
);

const Select = ({ value, onChange, options, ...rest }) => (
    <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
        {...rest}
    >
        <option value="">— select —</option>
        {options.map((o) =>
            typeof o === "string" ? (
                <option key={o} value={o}>
                    {o}
                </option>
            ) : (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ),
        )}
    </select>
);

const Check = ({ checked, onChange, label }) => (
    <label className="flex items-start gap-2 cursor-pointer">
        <input
            type="checkbox"
            checked={!!checked}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border/80 text-moss focus:ring-sage"
        />
        <span className="text-sm text-foreground/85">{label}</span>
    </label>
);

const Radio = ({ name, value, current, onChange, label }) => (
    <label className="flex items-start gap-2 cursor-pointer">
        <input
            type="radio"
            name={name}
            checked={current === value}
            onChange={() => onChange(value)}
            className="mt-1 h-4 w-4 border-border/80 text-moss focus:ring-sage"
        />
        <span className="text-sm text-foreground/85">{label}</span>
    </label>
);

const SectionTitle = ({ icon: Icon, title, hint }) => (
    <div className="mb-6">
        <div className="flex items-center gap-2">
            {Icon && (
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sand text-moss">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                </span>
            )}
            <h2 className="font-display text-2xl text-moss">{title}</h2>
        </div>
        {hint && (
            <p className="mt-2 max-w-2xl text-sm text-foreground/70 leading-relaxed">
                {hint}
            </p>
        )}
    </div>
);

/**
 * HighlightRing wraps a required field. When `invalidField === id`, it draws
 * a bold terracotta ring + shakes the field into view so the client cannot
 * possibly miss which answer is missing.
 */
const HighlightRing = ({ id, invalidField, children }) => {
    const active = invalidField === id;
    return (
        <div
            id={id}
            data-invalid={active ? "true" : undefined}
            className={
                active
                    ? "rounded-2xl ring-2 ring-terracotta ring-offset-2 ring-offset-cream animate-[pulse_1.2s_ease-in-out_2] p-1 -m-1"
                    : ""
            }
        >
            {children}
        </div>
    );
};

// ---- Step content components ----

const Step1 = ({ form, set, invalidField }) => (
    <div>
        <SectionTitle
            icon={UserIcon}
            title="Welcome"
            hint="Let’s start with the basics. All your responses are private and only visible to Riley."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <HighlightRing id="field-first-name" invalidField={invalidField}>
                <Field label="First name" required>
                    <Text
                        data-testid="intake-first-name"
                        value={form.first_name}
                        onChange={(v) => set("first_name", v)}
                        autoFocus
                    />
                </Field>
            </HighlightRing>
            <HighlightRing id="field-last-name" invalidField={invalidField}>
                <Field label="Last name" required>
                    <Text
                        data-testid="intake-last-name"
                        value={form.last_name}
                        onChange={(v) => set("last_name", v)}
                    />
                </Field>
            </HighlightRing>
            <HighlightRing id="field-email" invalidField={invalidField}>
                <Field label="Email" required>
                    <Text
                        data-testid="intake-email"
                        type="email"
                        value={form.email}
                        onChange={(v) => set("email", v)}
                    />
                </Field>
            </HighlightRing>
            <HighlightRing id="field-phone" invalidField={invalidField}>
                <Field label="Phone" required>
                    <Text
                        data-testid="intake-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(v) => set("phone", v)}
                    />
                </Field>
            </HighlightRing>
            <Field label="Birthdate">
                <Text
                    type="date"
                    value={form.birthdate}
                    onChange={(v) => set("birthdate", v)}
                />
            </Field>
            <Field label="Age">
                <Text value={form.age} onChange={(v) => set("age", v)} />
            </Field>
            <Field label="Sex assigned at birth">
                <Select
                    value={form.sex_assigned_at_birth}
                    onChange={(v) => set("sex_assigned_at_birth", v)}
                    options={["Female", "Male", "Intersex", "Prefer not to say"]}
                />
            </Field>
            <Field label="Gender identity">
                <Text
                    value={form.gender_identity}
                    onChange={(v) => set("gender_identity", v)}
                    placeholder="E.g. Woman, Man, Non-binary, etc."
                />
            </Field>
            <Field label="Street address">
                <Text
                    value={form.address_line1}
                    onChange={(v) => set("address_line1", v)}
                />
            </Field>
            <Field label="City">
                <Text value={form.city} onChange={(v) => set("city", v)} />
            </Field>
            <Field label="State / Region">
                <Text value={form.state} onChange={(v) => set("state", v)} />
            </Field>
            <Field label="Postal code">
                <Text
                    value={form.postal_code}
                    onChange={(v) => set("postal_code", v)}
                />
            </Field>
            <Field label="Occupation">
                <Text
                    value={form.occupation}
                    onChange={(v) => set("occupation", v)}
                />
            </Field>
            <Field label="Marital status">
                <Select
                    value={form.marital_status}
                    onChange={(v) => set("marital_status", v)}
                    options={["Single", "Partnered", "Married", "Divorced", "Widowed", "Other"]}
                />
            </Field>
            <Field label="Number of children">
                <Text
                    value={form.children_count}
                    onChange={(v) => set("children_count", v)}
                />
            </Field>
            <Field label="Children’s ages">
                <Text
                    value={form.children_ages}
                    onChange={(v) => set("children_ages", v)}
                />
            </Field>
        </div>
        <div className="mt-6 rounded-2xl border border-border/70 bg-sand/40 p-5">
            <p className="font-accent text-xs text-terracotta">EMERGENCY CONTACT</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Name">
                    <Text
                        value={form.emergency_contact?.name}
                        onChange={(v) => set("emergency_contact.name", v)}
                    />
                </Field>
                <Field label="Phone">
                    <Text
                        value={form.emergency_contact?.phone}
                        onChange={(v) => set("emergency_contact.phone", v)}
                    />
                </Field>
                <Field label="Relationship">
                    <Text
                        value={form.emergency_contact?.relationship}
                        onChange={(v) => set("emergency_contact.relationship", v)}
                    />
                </Field>
            </div>
        </div>
    </div>
);

const Step2 = ({ form, set, invalidField }) => (
    <div>
        <SectionTitle
            icon={FileText}
            title="Why are you here?"
            hint="Share what brought you to Choice Ayurveda and what you hope to feel different about."
        />
        <div className="space-y-4">
            <Field label="How did you hear about us?">
                <Text
                    value={form.how_heard}
                    onChange={(v) => set("how_heard", v)}
                    placeholder="Instagram, friend, web search, etc."
                />
            </Field>
            <HighlightRing id="field-primary-concern" invalidField={invalidField}>
                <Field label="Primary concern" required>
                    <TextArea
                        data-testid="intake-primary-concern"
                        value={form.primary_concern}
                        onChange={(v) => set("primary_concern", v)}
                        placeholder="In one or two sentences — what brings you in?"
                    />
                </Field>
            </HighlightRing>
            <Field label="Why are you seeking Ayurvedic care now?">
                <TextArea
                    value={form.why_seeking_care}
                    onChange={(v) => set("why_seeking_care", v)}
                    rows={4}
                />
            </Field>
            <Field label="Your goals — what does success look like to you?">
                <TextArea
                    value={form.goals}
                    onChange={(v) => set("goals", v)}
                    rows={4}
                />
            </Field>
        </div>
    </div>
);

const CONSENT_TEXT = {
    informed:
        "I understand that Riley Ann is a Certified Clinical Ayurvedic Practitioner and not a licensed physician. Ayurvedic services at Choice Ayurveda are educational and complementary in nature and are not a substitute for diagnosis, treatment, or medical advice from a licensed physician. I will continue to follow my physician’s recommendations and inform Riley of any medications, conditions, pregnancy, or significant changes in my health.",
    scope:
        "I understand the scope of Ayurvedic practice and release Choice Ayurveda and Riley Ann from liability for any outcome of services that are educational and lifestyle-based. I agree to disclose all relevant medical conditions and to communicate any adverse reactions promptly.",
    financial:
        "I agree to the financial policy: payment is due at time of service, 48 hours’ notice is required to cancel or reschedule, and cancellations within 24 hours may be charged 50% of the session fee. Packages are non-refundable but can be transferred.",
};

const Step3 = ({ form, set }) => (
    <div>
        <SectionTitle
            icon={AlertTriangle}
            title="Consents & agreements"
            hint="Three short consents. Please read each carefully and type your full name to sign."
        />
        {[
            { key: "informed_consent", sigKey: "informed_consent_signature", title: "Informed Consent", text: CONSENT_TEXT.informed },
            { key: "scope_of_practice", sigKey: "scope_signature", title: "Scope of Practice & Release", text: CONSENT_TEXT.scope },
            { key: "financial_policy", sigKey: "financial_signature", title: "Financial Policy", text: CONSENT_TEXT.financial },
        ].map((c) => (
            <div
                key={c.key}
                className="mb-4 rounded-2xl border border-border/80 bg-white/85 p-5"
            >
                <h3 className="font-display text-lg text-moss">{c.title}</h3>
                <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                    {c.text}
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <div className="sm:col-span-1">
                        <Check
                            checked={form.consent?.[c.key]}
                            onChange={(v) => set(`consent.${c.key}`, v)}
                            label={`I agree`}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Field label="Type your full name to sign">
                            <Text
                                value={form.consent?.[c.sigKey]}
                                onChange={(v) => set(`consent.${c.sigKey}`, v)}
                            />
                        </Field>
                    </div>
                </div>
            </div>
        ))}
        <Field label="Date signed">
            <Text
                type="date"
                value={form.consent?.signed_at}
                onChange={(v) => set("consent.signed_at", v)}
            />
        </Field>
    </div>
);

const Step4 = ({ form, set }) => (
    <div>
        <SectionTitle
            icon={HeartPulse}
            title="Medical & family history"
            hint="Helps Riley understand the full landscape — nothing is too small to mention."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Past illnesses (last 5 years)">
                <TextArea
                    value={form.medical_history?.past_illnesses}
                    onChange={(v) => set("medical_history.past_illnesses", v)}
                />
            </Field>
            <Field label="Hospitalizations">
                <TextArea
                    value={form.medical_history?.hospitalizations}
                    onChange={(v) => set("medical_history.hospitalizations", v)}
                />
            </Field>
            <Field label="Surgeries / operations">
                <TextArea
                    value={form.medical_history?.operations}
                    onChange={(v) => set("medical_history.operations", v)}
                />
            </Field>
            <Field label="Other significant medical conditions">
                <TextArea
                    value={form.medical_history?.other_conditions}
                    onChange={(v) => set("medical_history.other_conditions", v)}
                />
            </Field>
            <Field label="Chronic conditions you currently live with">
                <TextArea
                    value={form.medical_history?.chronic_conditions}
                    onChange={(v) => set("medical_history.chronic_conditions", v)}
                />
            </Field>
            <Field label="Are you pregnant or nursing?">
                <Select
                    value={form.medical_history?.pregnant_or_nursing}
                    onChange={(v) => set("medical_history.pregnant_or_nursing", v)}
                    options={["No", "Yes", "Possible", "Not applicable"]}
                />
            </Field>
            {form.medical_history?.pregnant_or_nursing === "Yes" && (
                <Field label="Pregnancy / nursing details">
                    <Text
                        value={form.medical_history?.pregnancy_details}
                        onChange={(v) => set("medical_history.pregnancy_details", v)}
                    />
                </Field>
            )}
        </div>
        <div className="mt-6">
            <p className="font-accent text-xs text-terracotta">FAMILY HISTORY (check all that apply)</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {FAMILY_OPTIONS.map((o) => (
                    <Check
                        key={o}
                        label={o}
                        checked={(form.medical_history?.family_history || []).includes(o)}
                        onChange={(v) => {
                            const cur = form.medical_history?.family_history || [];
                            const next = v ? [...cur, o] : cur.filter((x) => x !== o);
                            set("medical_history.family_history", next);
                        }}
                    />
                ))}
            </div>
            <div className="mt-3">
                <Field label="Other family conditions">
                    <Text
                        value={form.medical_history?.family_history_other}
                        onChange={(v) => set("medical_history.family_history_other", v)}
                    />
                </Field>
            </div>
        </div>
    </div>
);

const Step5 = ({ form, set }) => (
    <div>
        <SectionTitle
            icon={Wine}
            title="Substances, routines & relationships"
            hint="Honest answers help Riley craft a plan that meets you where you are."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Do you smoke?">
                <Select
                    value={form.substance_use?.smokes}
                    onChange={(v) => set("substance_use.smokes", v)}
                    options={["No", "Occasionally", "Daily", "Quit"]}
                />
            </Field>
            <Field label="If yes, what & how much?">
                <Text
                    value={form.substance_use?.smoking_details}
                    onChange={(v) => set("substance_use.smoking_details", v)}
                />
            </Field>
            <Field label="Alcohol">
                <Select
                    value={form.substance_use?.alcohol}
                    onChange={(v) => set("substance_use.alcohol", v)}
                    options={["None", "Occasional", "Few times a week", "Daily"]}
                />
            </Field>
            <Field label="Alcohol frequency / amount">
                <Text
                    value={form.substance_use?.alcohol_frequency}
                    onChange={(v) => set("substance_use.alcohol_frequency", v)}
                />
            </Field>
            <Field label="Recreational drugs">
                <Select
                    value={form.substance_use?.recreational_drugs}
                    onChange={(v) => set("substance_use.recreational_drugs", v)}
                    options={["None", "Occasionally", "Regularly", "Quit"]}
                />
            </Field>
            <Field label="If yes, details">
                <Text
                    value={form.substance_use?.drug_details}
                    onChange={(v) => set("substance_use.drug_details", v)}
                />
            </Field>
            <Field label="Caffeine">
                <Select
                    value={form.substance_use?.caffeine}
                    onChange={(v) => set("substance_use.caffeine", v)}
                    options={["None", "1 cup/day", "2–3 cups/day", "4+ cups/day"]}
                />
            </Field>
            <Field label="Caffeine source & timing">
                <Text
                    value={form.substance_use?.caffeine_amount}
                    onChange={(v) => set("substance_use.caffeine_amount", v)}
                />
            </Field>
        </div>

        <div className="mt-6">
            <p className="font-accent text-xs text-terracotta">REGULAR PRACTICES</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                    ["Exercise", "exercise"],
                    ["Yoga", "yoga"],
                    ["Meditation", "meditation"],
                    ["Pranayama / breathwork", "pranayama"],
                ].map(([label, key]) => (
                    <Field key={key} label={label}>
                        <Select
                            value={form.regular_practices?.[key]}
                            onChange={(v) => set(`regular_practices.${key}`, v)}
                            options={PRACTICE_FREQ}
                        />
                    </Field>
                ))}
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Other practice">
                    <Text
                        value={form.regular_practices?.other_practice_name}
                        onChange={(v) => set("regular_practices.other_practice_name", v)}
                    />
                </Field>
                <Field label="Frequency of other practice">
                    <Select
                        value={form.regular_practices?.other_practice}
                        onChange={(v) => set("regular_practices.other_practice", v)}
                        options={PRACTICE_FREQ}
                    />
                </Field>
            </div>
        </div>

        <div className="mt-6">
            <p className="font-accent text-xs text-terracotta">RELATIONSHIP LIFE</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Quality of close relationships (1–10)">
                    <Text
                        type="number"
                        min="1"
                        max="10"
                        value={form.relationship_life?.relationship_quality}
                        onChange={(v) => set("relationship_life.relationship_quality", v)}
                    />
                </Field>
                <Field label="Do you feel supported?">
                    <Select
                        value={form.relationship_life?.feels_supported}
                        onChange={(v) => set("relationship_life.feels_supported", v)}
                        options={["Yes", "Sometimes", "No"]}
                    />
                </Field>
                <Field label="Intimacy frequency (optional)">
                    <Text
                        value={form.relationship_life?.intimacy_frequency}
                        onChange={(v) => set("relationship_life.intimacy_frequency", v)}
                    />
                </Field>
                <Field label="Describe your social circle">
                    <Text
                        value={form.relationship_life?.social_circle}
                        onChange={(v) => set("relationship_life.social_circle", v)}
                    />
                </Field>
            </div>
        </div>
    </div>
);

const Step6 = ({ form, set }) => (
    <div>
        <SectionTitle
            icon={Apple}
            title="Diet, sleep & daily rhythm"
            hint="Ayurveda lives in the small daily choices — the more detail you share here, the better."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="How would you describe your diet?">
                <Text
                    value={form.diet?.diet_description}
                    onChange={(v) => set("diet.diet_description", v)}
                    placeholder="Omnivore, vegetarian, vegan, paleo, etc."
                />
            </Field>
            <Field label="Appetite">
                <Select
                    value={form.diet?.appetite}
                    onChange={(v) => set("diet.appetite", v)}
                    options={["Strong", "Moderate", "Variable", "Low", "None"]}
                />
            </Field>
            <Field label="Meals per day">
                <Text
                    value={form.diet?.meals_per_day}
                    onChange={(v) => set("diet.meals_per_day", v)}
                />
            </Field>
            <Field label="Water (oz/day)">
                <Text
                    value={form.diet?.water_oz_per_day}
                    onChange={(v) => set("diet.water_oz_per_day", v)}
                />
            </Field>
            <Field label="% organic">
                <Text
                    value={form.diet?.organic_percent}
                    onChange={(v) => set("diet.organic_percent", v)}
                    placeholder="e.g. 70%"
                />
            </Field>
            <Field label="Cravings">
                <Text
                    value={form.diet?.cravings}
                    onChange={(v) => set("diet.cravings", v)}
                />
            </Field>
            <Field label="Typical breakfast">
                <Text
                    value={form.diet?.typical_breakfast}
                    onChange={(v) => set("diet.typical_breakfast", v)}
                />
            </Field>
            <Field label="Typical lunch">
                <Text
                    value={form.diet?.typical_lunch}
                    onChange={(v) => set("diet.typical_lunch", v)}
                />
            </Field>
            <Field label="Typical dinner">
                <Text
                    value={form.diet?.typical_dinner}
                    onChange={(v) => set("diet.typical_dinner", v)}
                />
            </Field>
            <Field label="Snacks">
                <Text
                    value={form.diet?.snacks}
                    onChange={(v) => set("diet.snacks", v)}
                />
            </Field>
            <Field label="Foods that disagree with you">
                <TextArea
                    value={form.diet?.foods_that_disagree}
                    onChange={(v) => set("diet.foods_that_disagree", v)}
                />
            </Field>
        </div>
        <div className="mt-6">
            <p className="font-accent text-xs text-terracotta">DAILY SCHEDULE</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Wake time">
                    <Text
                        type="time"
                        value={form.daily_schedule?.wake_time}
                        onChange={(v) => set("daily_schedule.wake_time", v)}
                    />
                </Field>
                <Field label="Bed time">
                    <Text
                        type="time"
                        value={form.daily_schedule?.bed_time}
                        onChange={(v) => set("daily_schedule.bed_time", v)}
                    />
                </Field>
                <Field label="Sleep quality">
                    <Select
                        value={form.daily_schedule?.sleep_quality}
                        onChange={(v) => set("daily_schedule.sleep_quality", v)}
                        options={["Excellent", "Good", "Fair", "Poor", "Very poor"]}
                    />
                </Field>
                <Field label="Vivid dreams?">
                    <Text
                        value={form.daily_schedule?.dreams}
                        onChange={(v) => set("daily_schedule.dreams", v)}
                    />
                </Field>
                <Field label="Morning routine">
                    <TextArea
                        value={form.daily_schedule?.morning_routine}
                        onChange={(v) => set("daily_schedule.morning_routine", v)}
                    />
                </Field>
                <Field label="Workday routine">
                    <TextArea
                        value={form.daily_schedule?.workday_routine}
                        onChange={(v) => set("daily_schedule.workday_routine", v)}
                    />
                </Field>
                <Field label="Evening routine">
                    <TextArea
                        value={form.daily_schedule?.evening_routine}
                        onChange={(v) => set("daily_schedule.evening_routine", v)}
                    />
                </Field>
            </div>
        </div>
    </div>
);

const Step7 = ({ form, set }) => (
    <div>
        <SectionTitle
            icon={Pill}
            title="Allergies & medications"
            hint="List every medication, supplement, and herb you currently take."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Food allergies / sensitivities">
                <TextArea
                    value={form.allergies?.food_allergies}
                    onChange={(v) => set("allergies.food_allergies", v)}
                />
            </Field>
            <Field label="Drug allergies">
                <TextArea
                    value={form.allergies?.drug_allergies}
                    onChange={(v) => set("allergies.drug_allergies", v)}
                />
            </Field>
            <Field label="Environmental allergies">
                <TextArea
                    value={form.allergies?.environmental_allergies}
                    onChange={(v) => set("allergies.environmental_allergies", v)}
                />
            </Field>
            <Field label="Other allergies">
                <TextArea
                    value={form.allergies?.other}
                    onChange={(v) => set("allergies.other", v)}
                />
            </Field>
        </div>

        <div className="mt-6">
            <div className="flex items-center justify-between">
                <p className="font-accent text-xs text-terracotta">CURRENT MEDICATIONS, SUPPLEMENTS & HERBS</p>
                <button
                    type="button"
                    data-testid="intake-add-medication"
                    className="text-sm text-moss hover:text-terracotta"
                    onClick={() =>
                        set("medications", [
                            ...(form.medications || []),
                            {
                                _id: (typeof crypto !== "undefined" && crypto.randomUUID)
                                    ? crypto.randomUUID()
                                    : `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                                substance: "",
                                type: "",
                                prescribed_by: "",
                                purpose: "",
                                how_long: "",
                                dosage: "",
                                benefits: "",
                            },
                        ])
                    }
                >
                    + Add medication
                </button>
            </div>
            <div className="mt-3 space-y-3">
                {(form.medications || []).map((m, i) => (
                    <div
                        key={m._id || `med-${i}`}
                        className="rounded-2xl border border-border/80 bg-white/85 p-4"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Field label="Substance">
                                <Text
                                    value={m.substance}
                                    onChange={(v) =>
                                        set(
                                            "medications",
                                            form.medications.map((x, idx) =>
                                                idx === i ? { ...x, substance: v } : x,
                                            ),
                                        )
                                    }
                                />
                            </Field>
                            <Field label="Type">
                                <Select
                                    value={m.type}
                                    onChange={(v) =>
                                        set(
                                            "medications",
                                            form.medications.map((x, idx) =>
                                                idx === i ? { ...x, type: v } : x,
                                            ),
                                        )
                                    }
                                    options={["Prescription", "OTC", "Herb", "Supplement", "Vitamin"]}
                                />
                            </Field>
                            <Field label="Dosage">
                                <Text
                                    value={m.dosage}
                                    onChange={(v) =>
                                        set(
                                            "medications",
                                            form.medications.map((x, idx) =>
                                                idx === i ? { ...x, dosage: v } : x,
                                            ),
                                        )
                                    }
                                />
                            </Field>
                            <Field label="Purpose">
                                <Text
                                    value={m.purpose}
                                    onChange={(v) =>
                                        set(
                                            "medications",
                                            form.medications.map((x, idx) =>
                                                idx === i ? { ...x, purpose: v } : x,
                                            ),
                                        )
                                    }
                                />
                            </Field>
                            <Field label="Prescribed by">
                                <Text
                                    value={m.prescribed_by}
                                    onChange={(v) =>
                                        set(
                                            "medications",
                                            form.medications.map((x, idx) =>
                                                idx === i ? { ...x, prescribed_by: v } : x,
                                            ),
                                        )
                                    }
                                />
                            </Field>
                            <Field label="How long?">
                                <Text
                                    value={m.how_long}
                                    onChange={(v) =>
                                        set(
                                            "medications",
                                            form.medications.map((x, idx) =>
                                                idx === i ? { ...x, how_long: v } : x,
                                            ),
                                        )
                                    }
                                />
                            </Field>
                        </div>
                        <div className="mt-3 flex justify-end">
                            <button
                                type="button"
                                onClick={() =>
                                    set(
                                        "medications",
                                        form.medications.filter((_, idx) => idx !== i),
                                    )
                                }
                                className="text-xs text-terracotta hover:underline"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
                {(form.medications || []).length === 0 && (
                    <p className="text-sm text-foreground/55">
                        None added yet — click “+ Add medication” above to add a row.
                    </p>
                )}
            </div>
        </div>
    </div>
);

const DoshaGrid = ({ rows, form, set, basePath }) => (
    <div className="rounded-2xl border border-border/80 bg-white/85 overflow-hidden">
        <table className="w-full text-sm">
            <thead className="bg-sand/50">
                <tr>
                    <th className="text-left p-3 font-display text-moss">Quality</th>
                    <th className="text-left p-3 font-display text-moss">Vata</th>
                    <th className="text-left p-3 font-display text-moss">Pitta</th>
                    <th className="text-left p-3 font-display text-moss">Kapha</th>
                    <th className="text-left p-3 font-display text-moss">Discuss</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-border/70">
                {rows.map((r) => {
                    const current = form[basePath]?.[r.key];
                    return (
                        <tr key={r.key} className="align-top">
                            <td className="p-3 text-foreground/85 font-medium">{r.label}</td>
                            {[
                                ["vata", r.vata],
                                ["pitta", r.pitta],
                                ["kapha", r.kapha],
                                ["discuss", "Discuss with Riley"],
                            ].map(([val, label]) => (
                                <td key={val} className="p-3">
                                    <Radio
                                        name={`${basePath}-${r.key}`}
                                        value={val}
                                        current={current}
                                        onChange={(v) =>
                                            set(`${basePath}.${r.key}`, v)
                                        }
                                        label={label}
                                    />
                                </td>
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const Step8 = ({ form, set }) => (
    <div>
        <SectionTitle
            icon={Sparkles}
            title="Your Ayurvedic constitution"
            hint="For each row, choose the description that fits you most of your life. If you’re unsure, pick “Discuss with Riley.”"
        />
        <div className="overflow-x-auto">
            <DoshaGrid
                rows={DOSHA_ROWS}
                form={form}
                set={set}
                basePath="dosha_assessment"
            />
        </div>
        <div className="mt-4">
            <Field label="Anything you want Riley to know about your constitution?">
                <TextArea
                    value={form.dosha_assessment?.notes}
                    onChange={(v) => set("dosha_assessment.notes", v)}
                />
            </Field>
        </div>
    </div>
);

const Step9 = ({ form, set }) => (
    <div>
        <SectionTitle
            icon={Brain}
            title="Mental, emotional & current challenges"
            hint="Mind and body are one in Ayurveda. Share what’s present right now."
        />
        <div className="overflow-x-auto mb-6">
            <DoshaGrid
                rows={MENTAL_ROWS}
                form={form}
                set={set}
                basePath="mental_emotional"
            />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Current stress level (1–10)">
                <Text
                    type="number"
                    min="1"
                    max="10"
                    value={form.mental_emotional?.stress_level}
                    onChange={(v) => set("mental_emotional.stress_level", v)}
                />
            </Field>
            <Field label="Mental health history (optional)">
                <TextArea
                    value={form.mental_emotional?.mental_health_history}
                    onChange={(v) => set("mental_emotional.mental_health_history", v)}
                />
            </Field>
        </div>

        <div className="mt-6 rounded-2xl border border-border/70 bg-sand/40 p-5">
            <p className="font-accent text-xs text-terracotta">CHALLENGING PATTERNS</p>
            <p className="mt-1 text-xs text-foreground/65">
                List any current digestion, elimination, or emotional symptoms you experience. Add as many as you like.
            </p>
            {[
                ["Digestion", "digestion"],
                ["Elimination", "elimination"],
                ["Emotions", "emotions"],
                ["Other symptoms", "other_symptoms"],
                ["Diagnosed conditions", "diagnosed_conditions"],
            ].map(([title, key]) => (
                <div key={key} className="mt-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground/85">{title}</p>
                        <button
                            type="button"
                            className="text-xs text-moss hover:text-terracotta"
                            onClick={() =>
                                set(`challenging_patterns.${key}`, [
                                    ...(form.challenging_patterns?.[key] || []),
                                    {
                                        _id: (typeof crypto !== "undefined" && crypto.randomUUID)
                                            ? crypto.randomUUID()
                                            : `cp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                                        name: "",
                                        frequency: "",
                                        intensity: "",
                                    },
                                ])
                            }
                        >
                            + Add row
                        </button>
                    </div>
                    <div className="mt-2 space-y-2">
                        {(form.challenging_patterns?.[key] || []).map((row, i) => (
                            <div
                                key={row._id || `${key}-${i}`}
                                className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center"
                            >
                                <input
                                    placeholder="Symptom / pattern"
                                    value={row.name || ""}
                                    onChange={(e) => {
                                        const arr = [...(form.challenging_patterns?.[key] || [])];
                                        arr[i] = { ...arr[i], name: e.target.value };
                                        set(`challenging_patterns.${key}`, arr);
                                    }}
                                    className={`${inputClass} sm:col-span-6`}
                                />
                                <input
                                    type="number"
                                    min="1"
                                    max="3"
                                    placeholder="Freq 1-3"
                                    value={row.frequency || ""}
                                    onChange={(e) => {
                                        const arr = [...(form.challenging_patterns?.[key] || [])];
                                        arr[i] = { ...arr[i], frequency: e.target.value };
                                        set(`challenging_patterns.${key}`, arr);
                                    }}
                                    className={`${inputClass} sm:col-span-2`}
                                />
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    placeholder="Intensity 1-10"
                                    value={row.intensity || ""}
                                    onChange={(e) => {
                                        const arr = [...(form.challenging_patterns?.[key] || [])];
                                        arr[i] = { ...arr[i], intensity: e.target.value };
                                        set(`challenging_patterns.${key}`, arr);
                                    }}
                                    className={`${inputClass} sm:col-span-3`}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const arr = (form.challenging_patterns?.[key] || []).filter(
                                            (_, idx) => idx !== i,
                                        );
                                        set(`challenging_patterns.${key}`, arr);
                                    }}
                                    className="sm:col-span-1 text-xs text-terracotta hover:underline"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const Step10 = ({ form, set }) => {
    const showWomens =
        (form.sex_assigned_at_birth || "").toLowerCase() === "female" ||
        form.gender_identity?.toLowerCase().includes("woman");

    return (
        <div>
            <SectionTitle
                icon={CheckCircle2}
                title="Final details"
                hint="Almost done — one last set of questions."
            />
            {showWomens && (
                <div className="mb-6 rounded-2xl border border-border/70 bg-sand/40 p-5">
                    <p className="font-accent text-xs text-terracotta">WOMEN’S HEALTH</p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Are your periods regular?">
                            <Select
                                value={form.womens_health?.period_regular}
                                onChange={(v) => set("womens_health.period_regular", v)}
                                options={["Yes", "No", "Irregular", "N/A"]}
                            />
                        </Field>
                        <Field label="First day of last period">
                            <Text
                                type="date"
                                value={form.womens_health?.last_period_date}
                                onChange={(v) =>
                                    set("womens_health.last_period_date", v)
                                }
                            />
                        </Field>
                        <Field label="Cycle length (days)">
                            <Text
                                value={form.womens_health?.cycle_length_days}
                                onChange={(v) =>
                                    set("womens_health.cycle_length_days", v)
                                }
                            />
                        </Field>
                        <Field label="Currently pregnant?">
                            <Select
                                value={form.womens_health?.is_pregnant}
                                onChange={(v) => set("womens_health.is_pregnant", v)}
                                options={["No", "Yes", "Possible"]}
                            />
                        </Field>
                        <Field label="Ever been pregnant?">
                            <Select
                                value={form.womens_health?.has_been_pregnant}
                                onChange={(v) =>
                                    set("womens_health.has_been_pregnant", v)
                                }
                                options={["No", "Yes"]}
                            />
                        </Field>
                        <Field label="Menopause status">
                            <Select
                                value={form.womens_health?.menopause_status}
                                onChange={(v) =>
                                    set("womens_health.menopause_status", v)
                                }
                                options={[
                                    "Pre-menopausal",
                                    "Peri-menopausal",
                                    "Menopausal",
                                    "Post-menopausal",
                                    "N/A",
                                ]}
                            />
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label="Period symptoms / notes">
                                <TextArea
                                    value={form.womens_health?.notes}
                                    onChange={(v) => set("womens_health.notes", v)}
                                />
                            </Field>
                        </div>
                    </div>
                </div>
            )}
            <Field label="Anything else you’d like Riley to know?">
                <TextArea
                    value={form.additional_notes}
                    onChange={(v) => set("additional_notes", v)}
                    rows={5}
                />
            </Field>
        </div>
    );
};

const STEPS = [
    { id: 1, title: "Welcome", component: Step1 },
    { id: 2, title: "Why you’re here", component: Step2 },
    { id: 3, title: "Consents", component: Step3 },
    { id: 4, title: "Medical & family history", component: Step4 },
    { id: 5, title: "Substances & routines", component: Step5 },
    { id: 6, title: "Diet & daily rhythm", component: Step6 },
    { id: 7, title: "Allergies & medications", component: Step7 },
    { id: 8, title: "Constitution (dosha)", component: Step8 },
    { id: 9, title: "Mental & current challenges", component: Step9 },
    { id: 10, title: "Final details", component: Step10 },
];

export default function IntakePage() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState("wizard"); // "wizard" | "full"
    const [form, setForm] = useState(emptyForm());
    const [submitting, setSubmitting] = useState(false);
    const [saved, setSaved] = useState(null);
    // Which required field, if any, failed the last validation attempt. When set,
    // the form auto-jumps to that field's step, scrolls it into view, focuses it,
    // and draws a terracotta highlight ring so the client cannot miss it.
    const [invalidField, setInvalidField] = useState(null);
    // Populated when POST /api/intake fails so we can show a persistent modal
    // (never a fleeting toast) with Retry / Copy-my-answers options.
    const [submitError, setSubmitError] = useState(null);
    const draftIdRef = useRef(null);

    // On the standalone intake subdomain, the location is essentially "/" so
    // the breadcrumb back to "Home" makes no sense. Hide it in that mode.
    const isStandaloneHost =
        typeof window !== "undefined" &&
        (window.location.hostname.startsWith("intake.") ||
            new URLSearchParams(window.location.search).get("intake") === "1");
    const breadcrumbs = isStandaloneHost
        ? []
        : [{ label: "Home", to: "/" }, { label: "Intake" }];

    // Restore draft on mount
    useEffect(() => {
        try {
            const dId = localStorage.getItem(LS_DRAFT_ID);
            if (dId) draftIdRef.current = dId;
            const raw = localStorage.getItem(LS_DRAFT_DATA);
            if (raw) {
                const parsed = JSON.parse(raw);
                setForm((cur) => ({ ...cur, ...parsed }));
            }
        } catch (err) {
            // Draft restore is best-effort \u2014 don't block the form if storage is unavailable
            console.warn("Intake: failed to restore draft from storage:", err);
        }
        // setForm/refs are stable; running once on mount is intentional.
    }, []);

    // Auto-save (debounced) \u2014 fires when the form changes
    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                localStorage.setItem(LS_DRAFT_DATA, JSON.stringify(form));
                const { data } = await axios.post(`${API}/intake/draft`, {
                    draft_id: draftIdRef.current,
                    data: form,
                });
                if (data?.draft_id && !draftIdRef.current) {
                    draftIdRef.current = data.draft_id;
                    localStorage.setItem(LS_DRAFT_ID, data.draft_id);
                }
                setSaved(new Date());
            } catch (err) {
                // Network/storage failures are surfaced in the console but should not
                // interrupt the client filling out the form.
                console.warn("Intake: draft auto-save failed:", err);
            }
        }, 1500);
        return () => clearTimeout(t);
    }, [form]);

    // Path-based setter supports dotted paths (e.g. "emergency_contact.name").
    // Stable identity via useCallback so child memoization is safe.
    const set = useCallback((path, value) => {
        setForm((cur) => {
            const next = JSON.parse(JSON.stringify(cur));
            const keys = path.split(".");
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) {
                if (obj[keys[i]] == null) obj[keys[i]] = {};
                obj = obj[keys[i]];
            }
            obj[keys[keys.length - 1]] = value;
            return next;
        });
        // If the user is editing the field that was flagged invalid, clear the
        // highlight now so it doesn't linger visually.
        const invalidPathMap = {
            first_name: "field-first-name",
            last_name: "field-last-name",
            email: "field-email",
            phone: "field-phone",
            primary_concern: "field-primary-concern",
        };
        if (invalidPathMap[path]) {
            setInvalidField((prev) =>
                prev === invalidPathMap[path] ? null : prev,
            );
        }
    }, []);

    /**
     * Recursively coerce empty-string numeric fields to null so Pydantic
     * Optional[int] fields (relationship_quality, stress_level,
     * SymptomEntry.frequency & intensity) don't 422 on the backend.
     *
     * This is a client-side belt-and-suspenders defence: the backend also
     * coerces via field validators as of the fix on 2026-08-07.
     */
    const cleanPayload = (raw) => {
        const NUM_KEYS = new Set([
            "relationship_quality",
            "stress_level",
            "frequency",
            "intensity",
        ]);
        const walk = (val) => {
            if (Array.isArray(val)) return val.map(walk);
            if (val && typeof val === "object") {
                const out = {};
                for (const [k, v] of Object.entries(val)) {
                    // Strip client-only ids that would clutter the DB row.
                    if (k === "_id") continue;
                    if (NUM_KEYS.has(k) && typeof v === "string" && v.trim() === "") {
                        out[k] = null;
                    } else {
                        out[k] = walk(v);
                    }
                }
                return out;
            }
            return val;
        };
        return walk(raw);
    };

    /**
     * Return { ok, step, fieldId, message } so the caller can jump to the exact
     * step + field that failed validation instead of showing a toast the user
     * can't act on. Order of checks mirrors the form flow.
     */
    const validate = () => {
        if (!form.first_name)
            return { ok: false, step: 1, fieldId: "field-first-name", message: "Please enter your first name." };
        if (!form.last_name)
            return { ok: false, step: 1, fieldId: "field-last-name", message: "Please enter your last name." };
        if (!form.email || !form.email.includes("@"))
            return { ok: false, step: 1, fieldId: "field-email", message: "Please enter a valid email address." };
        if (!form.phone)
            return { ok: false, step: 1, fieldId: "field-phone", message: "Please enter a phone number." };
        if (!form.primary_concern)
            return { ok: false, step: 2, fieldId: "field-primary-concern", message: "Please describe your primary concern." };
        return { ok: true };
    };

    // After invalidField is set, scroll to it & focus the first input inside.
    // Runs in a layout-safe timeout so the target step has time to render.
    useEffect(() => {
        if (!invalidField) return;
        const t = setTimeout(() => {
            const el = document.getElementById(invalidField);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            const input = el.querySelector("input, textarea");
            if (input) {
                try {
                    input.focus({ preventScroll: true });
                } catch {
                    input.focus();
                }
            }
        }, 200);
        return () => clearTimeout(t);
    }, [invalidField, step, mode]);

    const doSubmit = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            const payload = cleanPayload(form);
            const { data } = await axios.post(`${API}/intake`, payload);
            // Clear draft
            localStorage.removeItem(LS_DRAFT_DATA);
            localStorage.removeItem(LS_DRAFT_ID);
            toast.success("Intake submitted!");
            navigate(`/intake/success?id=${data.intake_id || ""}`);
        } catch (e) {
            console.error("Intake submit failed:", e);
            const detail = e?.response?.data?.detail;
            const auditRef =
                (detail && typeof detail === "object" && detail.audit_ref) || null;
            const message =
                (detail && typeof detail === "object" && detail.message) ||
                (typeof detail === "string" && detail) ||
                e?.message ||
                "We couldn't reach the server.";
            setSubmitError({
                message,
                auditRef,
                statusCode: e?.response?.status,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const submit = async () => {
        const v = validate();
        if (!v.ok) {
            // Jump to the step containing the invalid field (wizard mode only).
            if (mode === "wizard") setStep(v.step);
            setInvalidField(v.fieldId);
            toast.error(v.message);
            return;
        }
        await doSubmit();
    };

    const copyAnswersToClipboard = async () => {
        try {
            const text = JSON.stringify(form, null, 2);
            await navigator.clipboard.writeText(text);
            toast.success("Your answers were copied to your clipboard.");
        } catch (e) {
            toast.error("Couldn't copy \u2014 please screenshot instead.");
        }
    };

    const StepComp = STEPS[step - 1].component;
    const progress = Math.round(((step - 1) / (STEPS.length - 1)) * 100);

    return (
        <div>
            <PageHeader
                eyebrow="NEW CLIENT INTAKE"
                title="Welcome to Choice Ayurveda"
                subtitle="This intake helps Riley understand you and prepare a personalized care plan. It takes ~15–20 minutes. Your answers auto-save so you can come back any time."
                breadcrumbs={breadcrumbs}
            >
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setMode(mode === "wizard" ? "full" : "wizard")}
                        data-testid="intake-mode-toggle"
                        className="inline-flex items-center gap-2 rounded-xl bg-cream border border-border/80 px-4 py-2 text-sm text-moss hover:border-moss/40"
                    >
                        {mode === "wizard" ? (
                            <>
                                <List className="h-4 w-4" /> See full form
                            </>
                        ) : (
                            <>
                                <LayoutGrid className="h-4 w-4" /> Switch to wizard
                            </>
                        )}
                    </button>
                    {saved && (
                        <span className="text-xs text-foreground/55 inline-flex items-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5 text-sage" />
                            Auto-saved at {saved.toLocaleTimeString()}
                        </span>
                    )}
                </div>
            </PageHeader>

            {mode === "wizard" ? (
                <section className="py-10">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                        {/* Progress bar */}
                        <div className="mb-8" data-testid="intake-progress">
                            <div className="flex items-center justify-between text-xs text-foreground/70">
                                <span>
                                    Step {step} of {STEPS.length} · {STEPS[step - 1].title}
                                </span>
                                <span>{progress}%</span>
                            </div>
                            <div className="mt-2 h-1.5 rounded-full bg-sand overflow-hidden">
                                <div
                                    className="h-full bg-moss transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>

                        <div className="rounded-3xl border border-border/80 bg-white/85 p-6 sm:p-10">
                            <StepComp form={form} set={set} invalidField={invalidField} />
                        </div>

                        <div className="mt-6 flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
                            <button
                                type="button"
                                onClick={() => setStep((s) => Math.max(1, s - 1))}
                                disabled={step === 1}
                                data-testid="intake-back"
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-moss disabled:text-foreground/30"
                            >
                                <ChevronLeft className="h-4 w-4" /> Back
                            </button>
                            {step < STEPS.length ? (
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
                                    data-testid="intake-next"
                                    className="inline-flex items-center gap-2 rounded-xl bg-moss text-cream px-5 py-3 text-sm font-medium hover:bg-moss/90"
                                >
                                    Continue <ChevronRight className="h-4 w-4" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="intake-submit"
                                    className="inline-flex items-center gap-2 rounded-xl bg-terracotta text-cream px-6 py-3 text-sm font-medium hover:bg-terracotta/90 disabled:opacity-60"
                                >
                                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Submit intake
                                </button>
                            )}
                        </div>
                    </div>
                </section>
            ) : (
                <section className="py-10">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-10">
                        {STEPS.map(({ id, title, component: C }) => (
                            <div
                                key={`step-${id}-${title}`}
                                className="rounded-3xl border border-border/80 bg-white/85 p-6 sm:p-10"
                            >
                                <C form={form} set={set} invalidField={invalidField} />
                            </div>
                        ))}
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={submit}
                                disabled={submitting}
                                data-testid="intake-submit-full"
                                className="inline-flex items-center gap-2 rounded-xl bg-terracotta text-cream px-6 py-3 text-sm font-medium hover:bg-terracotta/90 disabled:opacity-60"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                Submit intake
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* Persistent failure dialog \u2014 replaces the fleeting toast so a
                network/server error can never silently swallow a submission. */}
            <Dialog
                open={!!submitError}
                onOpenChange={(open) => {
                    if (!open) setSubmitError(null);
                }}
            >
                <DialogContent data-testid="intake-submit-error-dialog" className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-terracotta">
                            We couldn’t submit your intake
                        </DialogTitle>
                        <DialogDescription className="text-foreground/80 leading-relaxed">
                            <span className="block">
                                {submitError?.message ||
                                    "We couldn\u2019t reach the server. Your answers are safely saved on this device \u2014 nothing has been lost."}
                            </span>
                            {submitError?.auditRef && (
                                <span className="mt-3 block rounded-xl bg-sand/60 px-3 py-2 text-xs text-foreground/70">
                                    Reference:{" "}
                                    <code className="font-mono text-foreground/85">
                                        {submitError.auditRef}
                                    </code>
                                    <br />
                                    Please share this with Riley if you contact us for support.
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-2 gap-2 sm:gap-2">
                        <button
                            type="button"
                            onClick={copyAnswersToClipboard}
                            data-testid="intake-error-copy"
                            className="rounded-xl border border-border/80 bg-cream px-4 py-2 text-sm text-moss hover:border-moss/40"
                        >
                            Copy my answers
                        </button>
                        <button
                            type="button"
                            onClick={() => setSubmitError(null)}
                            data-testid="intake-error-close"
                            className="rounded-xl border border-border/80 bg-white px-4 py-2 text-sm text-foreground/70 hover:text-moss"
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            onClick={doSubmit}
                            disabled={submitting}
                            data-testid="intake-error-retry"
                            className="inline-flex items-center gap-2 rounded-xl bg-terracotta text-cream px-4 py-2 text-sm font-medium hover:bg-terracotta/90 disabled:opacity-60"
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            Retry submission
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
